import type { ImageField, RTNode } from "@prismicio/client"
import classNames from "classnames"
import React, { useRef } from "react"
import type { InViewHookResponse } from "react-intersection-observer"
import { Swiper, SwiperSlide } from "swiper/react"
import type { SwiperRef } from "swiper/react"
import type { SwiperOptions } from "swiper/types"

import { Analytics } from "@bounce/analytics"
import { Paragraph1, Title2 } from "@bounce/design-system"
import { theme } from "@bounce/theme"
import type { Nullable } from "@bounce/util"
import { SliderNavigation, useIsMobile } from "@bounce/web-components"

import type { PlaceCardProps } from "./PlaceCard"
import { PlaceCard } from "./PlaceCard"
import { InteractionEvents } from "../../../libs/analytics/events"

type Place = {
  href?: string
  image: Nullable<ImageField>
  name: string
  onClick?: () => void
  translatedName: string
  url: string
  content?: RTNode
}

export type PlaceSliderProps = {
  cardImageClassName?: string
  cardImageContainerClassName?: string
  cardTitleClassName?: string
  className?: string
  id?: string
  isPlaceCardSubtitleVisible: PlaceCardProps["isSubtitleVisible"]
  navigationClassName?: string
  places: Place[]
  sectionRef?: InViewHookResponse["ref"]
  subtitle?: string
  title?: string
}

const defaultBreakpoints = {
  slidesOffsetBefore: 24,
  slidesOffsetAfter: 0,
  slidesPerView: 1.5,
  spaceBetween: 0,
}

const breakpoints: SwiperOptions["breakpoints"] = {
  [theme.spacing("screen-md")]: {
    ...defaultBreakpoints,
    slidesPerView: 2,
  },
  [theme.spacing("screen-lg")]: {
    ...defaultBreakpoints,
    slidesPerView: 3,
  },
  [theme.spacing("screen-xl")]: {
    ...defaultBreakpoints,
    slidesPerView: 4,
  },
}

/**
 * Sorts places to prioritize those with content
 */
const sortPlacesByContent = (places: Place[]) => {
  return [...places].sort((a, b) => {
    if (a.content && !b.content) return -1
    if (!a.content && b.content) return 1
    return 0
  })
}

export const PlaceSlider = ({
  title,
  subtitle,
  places,
  id,
  sectionRef,
  className,
  navigationClassName,
  cardTitleClassName,
  cardImageClassName,
  cardImageContainerClassName,
  isPlaceCardSubtitleVisible = true,
}: PlaceSliderProps) => {
  const isMobile = useIsMobile()
  const swiperRef = useRef<SwiperRef>(null)

  const next = () => {
    swiperRef.current?.swiper.slideNext()
    Analytics.track(InteractionEvents.NextPoiPressed)
  }
  const prev = () => swiperRef.current?.swiper.slidePrev()
  const minPlacesSize = isMobile ? 1 : 3
  const enabled = places.length > minPlacesSize
  const onClickPlaceCard = (placeName: string) => {
    Analytics.track(InteractionEvents.PoiCardPressed, { placeName })
  }

  const sortedPlaces = sortPlacesByContent(places)

  return (
    <section
      id={id}
      ref={sectionRef}
      className={classNames("relative mx-auto flex w-full max-w-screen-xl flex-col", className)}>
      {!!title && <Title2 className="px-6 text-black">{title}</Title2>}
      {!!subtitle && <Paragraph1 className="mt-2 px-6 text-neutral-800 md:mt-2.5 xl:mt-3">{subtitle}</Paragraph1>}
      <div className="mt-8 md:mt-12">
        <Swiper
          ref={swiperRef}
          breakpoints={breakpoints}
          enabled={enabled}
          watchSlidesProgress
          rewind
          tag="ul"
          {...defaultBreakpoints}>
          {sortedPlaces.map(place => (
            <SwiperSlide key={place.url} onClick={() => onClickPlaceCard(place.name)} tag="li">
              <PlaceCard
                appUrl={place.url}
                href={place.href}
                image={place.image}
                name={place.translatedName}
                content={place.content}
                titleClassName={cardTitleClassName}
                imageClassName={cardImageClassName}
                imageContainerClassName={cardImageContainerClassName}
                isSubtitleVisible={isPlaceCardSubtitleVisible}
              />
            </SwiperSlide>
          ))}
        </Swiper>
        <SliderNavigation
          onClickNext={next}
          onClickPrev={prev}
          className={classNames("w-full justify-end px-6 pt-2", navigationClassName)}
          sliderItemSize={places.length}
          minSliderItemSizeToShow={minPlacesSize}
        />
      </div>
    </section>
  )
}
